import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ArrowLeft, ExternalLink, Loader2, SearchX } from 'lucide-react'
import { useTeamStore } from '../store/useTeamStore'
import useAppStore from '../../../store/useAppStore'
import TeamGameCard from '../components/TeamGameCard'
import TeamSessionItem from '../components/TeamSessionItem'
import { accentColor } from '../utils/teamColors'
import { modeLabel, roundPath, statsErrorText } from '../utils/teamStats'
import { CARD_CLASS, SECTION_VARIANT, SECTIONS_VARIANT } from '../utils/teamUi'
import useAudioManager from '../../../hooks/useAudioManager'

/**
 * Одна командная игра (`/teams/:slug/games/:id`, docs/team-stats-api.md §5).
 *
 * У сессии кейсов, апгрейдера и контракта своей страницы нет — экран строится
 * из `items` (все игры сессии по порядку, не больше 300: при обрезке приходит
 * `items_truncated: true`). У раунда `items: null` — туда ведёт `ref` на уже
 * существующую страницу карточки раунда, и этот экран остаётся сводкой.
 */
const TeamGameDetail = () => {
  const { slug, id } = useParams()
  const isGuest = useAppStore((state) => state.isGuest)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { playSound } = useAudioManager({ select: 'select' })
  const { team, loadTeam, game, isGameLoading, gameError, loadGame, resetGame } = useTeamStore()

  const gameSlug = slug === 'my' ? team?.id : slug

  useEffect(() => {
    loadTeam(slug)
    return () => resetGame()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isGuest])

  useEffect(() => {
    if (gameSlug && id) loadGame(gameSlug, id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameSlug, id, isGuest])

  const accent = accentColor(team?.colors)
  const items = Array.isArray(game?.items) ? game.items : null
  const roundHref = game && !game.is_session ? roundPath(game.ref) : null

  return (
    <div className="mx-auto w-full px-4 py-6 lg:max-w-3xl lg:px-6 lg:py-8">
      <motion.div variants={SECTIONS_VARIANT} initial="hidden" animate="visible" className="space-y-5">
        <motion.div variants={SECTION_VARIANT} className="flex items-center gap-3">
          <button
            onClick={() => {
              playSound('select')
              if (window.history.length > 1) navigate(-1)
              else navigate(`/teams/${slug}/games`)
            }}
            className="rounded-lg bg-surface p-2 text-white transition-all active:scale-[0.98]"
            aria-label={t('common.back', 'Назад')}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0">
            <h1 className="truncate text-2xl text-white">
              {game ? modeLabel(game, t) : t('team.stats.gameTitle', 'Командная игра')}
            </h1>
            {team?.name && <p className="truncate text-xs text-text-secondary">{team.name}</p>}
          </div>
        </motion.div>

        {gameError ? (
          <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} flex flex-col items-center p-8 text-center`}>
            <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-dark-primary">
              <SearchX size={24} className="text-text-secondary" />
            </span>
            <p className="text-sm text-text-secondary">{statsErrorText(gameError, t)}</p>
          </motion.div>
        ) : isGameLoading || !game ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-accent-primary" />
          </div>
        ) : (
          <>
            <motion.div variants={SECTION_VARIANT}>
              <TeamGameCard game={game} accent={accent} detailed />
            </motion.div>

            {/* Раунд: своя страница уже есть — ведём на неё, там видно все ставки */}
            {roundHref && (
              <motion.button
                variants={SECTION_VARIANT}
                onClick={() => {
                  playSound('select')
                  navigate(roundHref)
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl border p-3.5 text-sm font-bold transition-all active:scale-[0.98]"
                style={{ borderColor: `${accent}55`, color: accent, backgroundColor: `${accent}14` }}
              >
                <ExternalLink size={16} />
                {t('team.stats.openRound', 'Открыть раунд')}
              </motion.button>
            )}

            {items && (
              <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-4`}>
                <h2 className="mb-3 text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
                  {t('team.stats.sessionItems', 'Игры сессии')}
                </h2>

                <div className="space-y-2">
                  {items.map((item) => (
                    <TeamSessionItem key={item.id} item={item} accent={accent} />
                  ))}
                </div>

                {game.items_truncated && (
                  <p className="mt-3 text-center text-[11px] text-text-secondary">
                    {t('team.stats.itemsTruncated', 'Показаны первые 300 игр сессии')}
                  </p>
                )}
              </motion.div>
            )}
          </>
        )}
      </motion.div>
    </div>
  )
}

export default TeamGameDetail
